/* DreamCloud → Actions: the fixes an owner runs from the page.
 *
 * "Replay conversions" and "Upload call report" open their own dialogs
 * (dc-replay.js, dc-call-report.js). The rest sit under "More actions", and
 * each is a dry run first: choosing one runs its dry form and shows the
 * counts; "Apply" then sends the real form (dry_run: false, or commit: 1) over
 * the same dates the dry run reported. The worker's own *_DRY_RUN switches are
 * untouched, so an Apply can still come back as a dry run; that is said.
 *
 * "Re-check Google now" only re-reads the status of uploads Google is still
 * processing. It has no dry run and changes nothing of ours, so it runs
 * directly and says it is read-only.
 *
 * `replayRequest` is set by a "Sales were never sent" problem: it opens the
 * replay dialog over the dates the worker recorded.
 */
import * as Vue from 'vue';
import { dreamcloud } from '../dreamcloud.js';
import { resultCounts, errorText, applyFailureText, formatWhen } from '../rules/dreamcloud.js';
import { DcReplay } from './dc-replay.js';
import { DcCallReport } from './dc-call-report.js';

function isDay(v) { return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v); }

/* Over the window the dry run reported, when it reported one, so the run that
 * writes covers the days that were reviewed. */
function commitOver(dry) {
  var p = { commit: 1 };
  if (dry && isDay(dry.start) && isDay(dry.end)) { p.start = dry.start; p.end = dry.end; }
  return p;
}

var MORE = [
  { id: 'audience_sync', label: 'Re-sync audiences',
    blurb: 'The Customer Match lists in Google Ads, one batch.',
    dry: function () { return { dry_run: true }; },
    apply: function () { return { dry_run: false }; } },
  { id: 'phone_calls_sync', label: 'Re-sync phone calls',
    blurb: 'The business line’s call history from Mangomint.',
    dry: function () { return {}; }, apply: commitOver },
  { id: 'cancellations_reconcile', label: 'Reconcile cancellations',
    blurb: 'Appointment states against Mangomint.',
    dry: function () { return {}; }, apply: commitOver },
  { id: 'ads_status_sweep', label: 'Re-check Google now (read-only)',
    blurb: 'Asks Google again about uploads it is still processing.',
    readOnly: true }
];

function said(err) { return errorText(err); }

export const DcActions = {
  name: 'DcActions',
  components: { DcReplay: DcReplay, DcCallReport: DcCallReport },
  props: { replayRequest: { type: Object, default: null } },
  setup: function (props) {
    var state = dreamcloud.state;
    var dialog = Vue.ref(null);              // 'replay' | 'calls' | null
    var replayDates = Vue.ref({ start: null, end: null, key: 0 });

    /* A write on its way: the open dialog's send or store (it says so with
     * `busy`), or an Apply here. Nothing may replace or remount the dialog
     * then, or its outcome would be lost; the buttons that would are off. */
    var dialogBusy = Vue.ref(false);
    var busy = Vue.computed(function () { return dialogBusy.value || state.writing > 0; });

    function openDialog(which) {
      if (busy.value) return;
      dialog.value = which;
    }

    function openReplay(start, end) {
      if (busy.value) return;
      replayDates.value = { start: start || null, end: end || null, key: replayDates.value.key + 1 };
      dialog.value = 'replay';
    }

    function closeDialog() {
      if (dialogBusy.value) return;
      dialog.value = null;
    }

    Vue.watch(function () { return props.replayRequest; }, function (r) {
      if (r) openReplay(r.start, r.end);
    });

    var menuOpen = Vue.ref(false);

    /* The one More-actions panel open: which action, where it is, and what came
     * back. phase: dry → reviewed → applying → applied | failed. */
    var act = Vue.reactive({ id: null, phase: null, dry: null, result: null, error: null, failedBody: null });
    var seq = 0;

    function current() {
      return MORE.find(function (m) { return m.id === act.id; }) || null;
    }

    async function choose(m) {
      if (busy.value) return;
      menuOpen.value = false;
      var mine = ++seq;
      Object.assign(act, { id: m.id, phase: 'dry', dry: null, result: null, error: null, failedBody: null });
      try {
        var r = await dreamcloud.invoke(m.id, m.readOnly ? {} : m.dry());
        if (mine !== seq) return;
        if (m.readOnly) { Object.assign(act, { phase: 'applied', result: r }); dreamcloud.load(); }
        else Object.assign(act, { phase: 'reviewed', dry: r });
      } catch (err) {
        if (mine === seq) Object.assign(act, { phase: 'failed', error: said(err) });
      }
    }

    async function apply() {
      var m = current();
      if (!m || m.readOnly || act.phase !== 'reviewed') return;
      var mine = ++seq;
      act.phase = 'applying';
      dreamcloud.beginWrite();
      var outcome;
      try {
        var r = await dreamcloud.invoke(m.id, m.apply(act.dry));
        outcome = r && r.commit_requested && r.mode === 'dry_run'
          ? { ok: false, text: m.label + ': not applied (DreamCloud’s dry-run switch is on).' }
          : { ok: true, text: m.label + ': applied.' };
        if (mine === seq) Object.assign(act, { phase: 'applied', result: r });
      } catch (err) {
        // A failed write can still have done part of its work (audience sync
        // answers 502 with its counts and errors[] when some ops failed), and a
        // timeout says nothing either way: every thrown Apply says it may
        // have been partly applied. The outcome line carries the counts; the
        // panel lists them under the sentence.
        outcome = { ok: false, text: m.label + ': ' + applyFailureText(err, { counts: true }) };
        if (mine === seq) Object.assign(act, { phase: 'failed', error: applyFailureText(err), failedBody: err && err.body ? err.body : null });
      } finally {
        dreamcloud.endWrite(outcome);
      }
      // The run shows under Scheduled jobs as a manual one.
      dreamcloud.load();
    }

    function dismiss() {
      if (act.phase === 'applying') return;
      seq++;
      Object.assign(act, { id: null, phase: null, dry: null, result: null, error: null, failedBody: null });
    }

    /* The worker can be asked to commit and still not write, when its own
     * dry-run switch is on. Its answer says so in `mode`. */
    var stillDry = Vue.computed(function () {
      var r = act.result;
      return !!(r && r.commit_requested && r.mode === 'dry_run');
    });

    var errors = function (r) { return r && Array.isArray(r.errors) ? r.errors.filter(Boolean) : []; };

    /* The last write's outcome, from the store: still there when the owner
     * comes back to the page after leaving it mid-send. Shown while no dialog
     * or panel is already saying it. */
    var lastWrite = Vue.computed(function () {
      return dialog.value || act.id ? null : state.lastWrite;
    });

    return {
      busy: busy, dialogBusy: dialogBusy, openDialog: openDialog, closeDialog: closeDialog,
      lastWrite: lastWrite, when: formatWhen, clearLast: function () { state.lastWrite = null; },
      dialog: dialog, replayDates: replayDates, openReplay: openReplay,
      menuOpen: menuOpen, MORE: MORE, act: act, current: current, choose: choose, apply: apply, dismiss: dismiss,
      stillDry: stillDry, counts: resultCounts, errors: errors
    };
  },
  template: [
    '<section class="dc-section dc-card dc-actions" id="dc-actions" aria-labelledby="dc-actions-h"',
    '  data-slot="actions">',
    '  <h3 class="dc-h" id="dc-actions-h">Actions</h3>',
    '  <p class="dc-muted dc-actions__lead">Each one runs a dry run first. Nothing is sent or written until you confirm.</p>',
    '  <div class="dc-actions__main">',
    '    <button type="button" class="btn btn--primary" :disabled="busy" @click="openReplay(null, null)">Replay conversions</button>',
    '    <button type="button" class="btn btn--primary" :disabled="busy" @click="openDialog(\'calls\')">Upload call report</button>',
    '  </div>',
    '  <p v-if="lastWrite" class="dc-last" :class="lastWrite.ok ? \'is-ok\' : \'is-failed\'" role="status">',
    '    <span>{{ lastWrite.text }} <span class="dc-muted">({{ when(lastWrite.at) }})</span></span>',
    '    <button type="button" class="dc-x dc-x--sm" aria-label="Dismiss" @click="clearLast">&times;</button>',
    '  </p>',
    '',
    '  <div class="dc-more">',
    '    <button type="button" class="btn btn--sm dc-btn" :disabled="busy" :aria-expanded="menuOpen ? \'true\' : \'false\'"',
    '      aria-controls="dc-more-menu" @click="menuOpen = !menuOpen">More actions &#9662;</button>',
    '    <ul v-if="menuOpen" id="dc-more-menu" class="dc-more__menu">',
    '      <li v-for="m in MORE" :key="m.id">',
    '        <button type="button" class="dc-more__item" @click="choose(m)" :disabled="busy">',
    '          <strong>{{ m.label }}</strong><span class="dc-muted">{{ m.blurb }}</span></button>',
    '      </li>',
    '    </ul>',
    '  </div>',
    '',
    '  <div v-if="act.id" class="dc-act" role="region" :aria-label="current().label">',
    '    <p class="dc-act__title"><strong>{{ current().label }}</strong>',
    '      <span v-if="act.phase === \'dry\'" class="dc-muted">{{ current().readOnly ? \'Asking Google…\' : \'Running the dry run…\' }}</span>',
    '      <span v-else-if="act.phase === \'reviewed\'" class="dc-muted">Dry run: nothing written</span>',
    '      <span v-else-if="act.phase === \'applying\'" class="dc-muted">Applying…</span>',
    '      <span v-else-if="act.phase === \'applied\' && !current().readOnly && stillDry" class="dc-warn">Not applied (dry-run switch on)</span>',
    '      <span v-else-if="act.phase === \'applied\' && !current().readOnly" class="dc-ok">Applied</span>',
    '    </p>',
    '    <dl v-if="act.phase === \'reviewed\' || act.phase === \'applying\'" class="dc-counts">',
    '      <div v-for="c in counts(act.dry)" :key="c.label"><dt>{{ c.label }}</dt><dd>{{ c.value }}</dd></div>',
    '    </dl>',
    '    <dl v-if="act.phase === \'applied\'" class="dc-counts">',
    '      <div v-for="c in counts(act.result)" :key="c.label"><dt>{{ c.label }}</dt><dd>{{ c.value }}</dd></div>',
    '    </dl>',
    '    <p v-if="stillDry" class="dc-warn">DreamCloud&rsquo;s own dry-run switch for this job is on, so nothing was written.</p>',
    '    <ul v-if="errors(act.phase === \'applied\' ? act.result : act.dry).length" class="dc-act__errors">',
    '      <li v-for="(e, i) in errors(act.phase === \'applied\' ? act.result : act.dry)" :key="i" class="dc-error">{{ e }}</li>',
    '    </ul>',
    '    <p v-if="act.phase === \'failed\'" class="dc-error" role="alert">{{ act.error }}</p>',
    /* A failed write's own answer: what it did before it failed. */
    '    <template v-if="act.phase === \'failed\' && act.failedBody">',
    '      <p class="dc-muted">What DreamCloud reported doing before it failed:</p>',
    '      <dl class="dc-counts">',
    '        <div v-for="c in counts(act.failedBody)" :key="c.label"><dt>{{ c.label }}</dt><dd>{{ c.value }}</dd></div>',
    '      </dl>',
    '      <ul v-if="errors(act.failedBody).length" class="dc-act__errors">',
    '        <li v-for="(e, i) in errors(act.failedBody)" :key="i" class="dc-error">{{ e }}</li>',
    '      </ul>',
    '    </template>',
    '    <div class="dc-act__btns">',
    '      <button v-if="act.phase === \'reviewed\' || act.phase === \'applying\'" type="button" class="btn btn--sm btn--danger"',
    '        :disabled="act.phase === \'applying\'" @click="apply">Apply</button>',
    '      <button type="button" class="btn btn--sm btn--quiet" :disabled="act.phase === \'applying\'" @click="dismiss">',
    '        {{ act.phase === \'reviewed\' ? \'Cancel\' : \'Close\' }}</button>',
    '    </div>',
    '  </div>',
    '',
    '  <dc-replay v-if="dialog === \'replay\'" :key="replayDates.key" :start="replayDates.start" :end="replayDates.end"',
    '    @close="closeDialog" @busy="dialogBusy = $event" />',
    '  <dc-call-report v-if="dialog === \'calls\'" @close="closeDialog" @busy="dialogBusy = $event" />',
    '</section>'
  ].join('\n')
};
